<template>
    <BaseModal
        :title="isEditing ? 'Edit Major' : 'Add Major'"
        :loading="loading"
        :submit-text="isEditing ? 'Update Major' : 'Create Major'"
        max-width="2xl"
        @close="emit('close')"
        @submit="handleSubmit"
    >
        <form @submit.prevent="handleSubmit" class="space-y-6">
            <!-- Code -->
            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Code
                </label>
                <input
                    v-model="formData.code"
                    type="text"
                    maxlength="50"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                    placeholder="e.g., IPA, IPS, BAHASA"
                    required
                />
                <div
                    v-if="errors.code"
                    class="mt-1 text-sm text-red-600 dark:text-red-400"
                >
                    {{ errors.code[0] }}
                </div>
            </div>

            <!-- Name -->
            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Name
                </label>
                <input
                    v-model="formData.name"
                    type="text"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                    placeholder="e.g., Science, Social Studies, Language"
                    required
                />
                <div
                    v-if="errors.name"
                    class="mt-1 text-sm text-red-600 dark:text-red-400"
                >
                    {{ errors.name[0] }}
                </div>
            </div>

            <!-- Description -->
            <div>
                <label
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                >
                    Description
                </label>
                <textarea
                    v-model="formData.description"
                    rows="4"
                    class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white transition-colors"
                    placeholder="Optional major description"
                ></textarea>
                <div
                    v-if="errors.description"
                    class="mt-1 text-sm text-red-600 dark:text-red-400"
                >
                    {{ errors.description[0] }}
                </div>
            </div>

            <!-- Active Status -->
            <div class="bg-gray-50 dark:bg-gray-700/50 p-4 rounded-md">
                <label
                    class="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer"
                >
                    <input
                        v-model="formData.is_active"
                        type="checkbox"
                        id="is_active"
                        class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded transition-colors mr-3"
                    />
                    <span> Active Status </span>
                </label>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Enable this major for class and subject assignments
                </p>
                <div
                    v-if="errors.is_active"
                    class="mt-1 text-sm text-red-600 dark:text-red-400"
                >
                    {{ errors.is_active[0] }}
                </div>
            </div>
        </form>
    </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useMajorStore } from "@/stores/majorStore";
import { majorApi } from "@/api/major";
import BaseModal from "@/components/BaseModal.vue";

interface MajorFormData {
    code: string;
    name: string;
    description?: string;
    is_active?: boolean;
}

// Props
const props = defineProps<{
    major?: any;
    isEditing: boolean;
    showModal: boolean;
}>();

const emit = defineEmits<{
    close: [];
    save: [majorData: any];
    saved: [];
}>();

const majorStore = useMajorStore();

// Form data
const formData = ref<MajorFormData>({
    code: "",
    name: "",
    description: "",
    is_active: true,
});

const errors = ref<Record<string, string[]>>({});

// Watch for major changes
watch(
    () => props.major,
    (newMajor) => {
        if (newMajor) {
            formData.value = {
                code: newMajor.code || "",
                name: newMajor.name || "",
                description: newMajor.description || "",
                is_active:
                    newMajor.is_active !== undefined
                        ? newMajor.is_active
                        : true,
            };
        }
    },
    { immediate: true },
);

// Computed
const loading = computed(() => majorStore.loading);

// Methods
const handleSubmit = async () => {
    try {
        // Convert code to uppercase before submitting
        if (formData.value.code) {
            formData.value.code = formData.value.code.toUpperCase();
        }

        // Ensure is_active is a boolean value
        const submitData = {
            ...formData.value,
            is_active: Boolean(formData.value.is_active),
        };

        if (props.isEditing && props.major) {
            await majorStore.updateMajor(props.major.id, submitData);
        } else {
            await majorStore.createMajor(submitData);
        }

        emit("saved");
    } catch (error: any) {
        console.error("Error saving major:", error);

        // Handle validation errors from API response
        if (error.response?.status === 422) {
            errors.value = error.response.data.errors;
            return; // Do NOT throw upward for validation errors
        }
        // For unexpected errors, re-throw to let parent handle them
        throw error;
    }
};
</script>

<style scoped>
/* Form transitions */
input,
select,
textarea {
    transition:
        border-color 0.15s ease-in-out,
        box-shadow 0.15s ease-in-out,
        background-color 0.15s ease-in-out;
}

input:focus,
select:focus,
textarea:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Button transitions */
button {
    transition: all 0.2s ease-in-out;
}

/* Checkbox styling */
input[type="checkbox"]:checked {
    background-color: #3b82f6;
    border-color: #3b82f6;
}

/* Error states */
.border-red-500 {
    border-color: #ef4444;
}

.text-red-600 {
    color: #dc2626;
}

.dark .text-red-400 {
    color: #f87171;
}

/* Dark mode adjustments */
.dark input,
.dark select,
.dark textarea {
    background-color: #1f2937;
    border-color: #4b5563;
    color: #f9fafb;
}

.dark input:focus,
.dark select:focus,
.dark textarea:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Hover effects */
.hover\:bg-gray-50:hover {
    background-color: #f9fafb;
}

.dark .hover\:bg-gray-600:hover {
    background-color: #4b5563;
}

/* Focus ring styles */
.focus\:ring-blue-500:focus {
    --tw-ring-color: #3b82f6;
}

.focus\:ring-offset-2:focus {
    --tw-ring-offset-width: 2px;
}

/* Modal specific styles */
.max-w-2xl {
    max-width: 42rem;
}

/* Responsive design */
@media (max-width: 768px) {
    .max-w-2xl {
        max-width: 90vw;
    }
}
</style>
